/**
 * Чистая часть просмотра картинок крупно (docs/homework-image-viewer-plan.md,
 * Р1, Р5 и Р10): что слайд знает о своей загрузке, кому нужен адрес, какие
 * слайды отдать карусели и когда сообщить «картинку посмотрели». Видео и аудио
 * формы выдачи идут тем же путём (docs/homework-media-form-player-plan.md, Р1):
 * событие `imgError` означает отказ любого элемента слайда, и правило «один
 * перевыпуск, потом строка с повтором» у них то же.
 *
 * ⚠️ Пакета `yet-another-react-lightbox` модуль не знает: форма слайда здесь
 * своя, а в слайд пакета её переводит `ImageViewer.tsx`, единственный файл,
 * которому пакет разрешён (замок `src/image-viewer-barrier.test.ts`). Вызывающие
 * берут отсюда `import type` и `startIndex`: значением модуль лёгкий, данных и
 * зависимостей у него нет.
 */
import type { ViewerMediaKind, ViewerNoun } from "./viewer-text.ts";

/**
 * Видео или аудио материала (docs/homework-media-form-player-plan.md, Р1).
 * Поле необязательное: лента и фото решения его не ставят, и для них слой
 * остаётся прежним.
 */
export interface ViewerMedia {
  kind: ViewerMediaKind;
  /** Адрес кадра-обложки видео; `null`, если кадра нет или он протух. */
  poster: string | null;
  /** Имя файла под плеером. */
  title: string;
}

/** Слайд карусели, как его видит вызывающий: картинка, а с `media` видео или аудио. */
export interface ViewerImage {
  /** `fileId` у ленты и фото решения, ключ строки у формы. */
  key: string;
  /** «Картинка 2 из 5», «Видео «Имя»»: имя слайда для читалки. */
  alt: string;
  /** Если известна: коробка до загрузки и предел увеличения. */
  width: number | null;
  height: number | null;
  media?: ViewerMedia;
}

/** Адрес картинки. `null` значит «адреса нет», это отказ слайда. */
export type ResolveImage = (key: string, opts: { force: boolean }) => Promise<string | null>;

export interface ViewerRequest {
  /** Снимок в момент нажатия, в порядке материала. */
  images: readonly ViewerImage[];
  /** Индекс нажатой картинки в `images`: `startIndex(images, key)`. */
  start: number;
  resolve: ResolveImage;
  /** Куда вернуть фокус после закрытия. */
  opener: HTMLElement | null;
  /** Слова слоя: картинка материала или фотография решения. */
  noun: ViewerNoun;
  /**
   * Картинка ТЕКУЩЕГО слайда загрузилась (отметка фото, Р10). Зовётся не чаще
   * раза на ключ за жизнь слоя.
   */
  onView?: (key: string) => void;
}

/**
 * Сколько соседей с каждой стороны держать готовыми. Одно число на
 * `carousel.preload` пакета и на `indexesToLoad`: разойдись они, пакет рисовал
 * бы слайды, адрес которым никто не просил, или просили бы адрес слайду,
 * которого нет в DOM.
 */
export const VIEWER_PRELOAD = 1;

/**
 * Какие подписи пакета у слоя. Хоть одно видео или аудио в списке, и кнопки
 * листают уже не картинки, а файлы (Р6).
 */
export function labelSetOf(images: readonly ViewerImage[]): "images" | "mixed" {
  return images.some((image) => image.media !== undefined) ? "mixed" : "images";
}

/** Индекс картинки по ключу в СПИСКЕ КАРУСЕЛИ; нет такой, 0. */
export function startIndex(images: readonly ViewerImage[], key: string): number {
  const at = images.findIndex((image) => image.key === key);
  return at < 0 ? 0 : at;
}

/** Что слайд знает о своей картинке. */
export type SlideLoad =
  | { kind: "idle" }
  | { kind: "loading"; force: boolean; retried: boolean }
  | { kind: "ready"; src: string; retried: boolean }
  | { kind: "failed" };

export type SlideEvent =
  | { type: "need" }
  | { type: "resolved"; src: string | null }
  | { type: "imgError" }
  | { type: "retry" };

export const IDLE: SlideLoad = { kind: "idle" };

/**
 * Переход загрузки слайда. Правило то же, что у картинки ленты: отказ
 * картинки один раз перечитывает адреса, второй отказ это строка с повтором, а
 * повтор по кнопке перечитывает и даёт ещё одну попытку.
 *
 * Без изменений возвращается ТОТ ЖЕ объект: на нём держится то, что лишнее
 * событие не плодит нового состояния и нового рендера.
 */
export function nextLoad(state: SlideLoad, event: SlideEvent): SlideLoad {
  switch (event.type) {
    case "need":
      return state.kind === "idle" ? { kind: "loading", force: false, retried: false } : state;
    case "resolved":
      if (state.kind !== "loading") return state;
      return event.src === null ? { kind: "failed" } : { kind: "ready", src: event.src, retried: state.retried };
    case "imgError":
      if (state.kind !== "ready") return state;
      return state.retried ? { kind: "failed" } : { kind: "loading", force: true, retried: true };
    case "retry":
      return state.kind === "failed" ? { kind: "loading", force: true, retried: true } : state;
  }
}

/** Индексы, которым нужен адрес: текущий и соседи в пределах `preload`, без круга. */
export function indexesToLoad(index: number, total: number, preload: number): number[] {
  const out: number[] = [];
  for (let at = Math.max(0, index - preload); at <= Math.min(total - 1, index + preload); at += 1) out.push(at);
  return out;
}

/**
 * Слайд просмотрщика: готовая картинка, видео или аудио, либо коробка ожидания
 * и отказа. У ожидающего медиа вид нужен для слов («Загружаем видео»).
 */
export type ViewerSlide =
  | { kind: "image"; key: string; src: string; alt: string; width?: number; height?: number }
  | { kind: "video"; key: string; src: string; alt: string; poster?: string; title: string }
  | { kind: "audio"; key: string; src: string; alt: string; title: string }
  | { kind: "pending"; key: string; failed: boolean; media?: ViewerMediaKind };

function slideOf(image: ViewerImage, load: SlideLoad): ViewerSlide {
  const { media } = image;
  if (load.kind !== "ready") {
    const failed = load.kind === "failed";
    return media === undefined
      ? { kind: "pending", key: image.key, failed }
      : { kind: "pending", key: image.key, failed, media: media.kind };
  }
  if (media?.kind === "video") {
    return {
      kind: "video",
      key: image.key,
      src: load.src,
      alt: image.alt,
      ...(media.poster !== null ? { poster: media.poster } : {}),
      title: media.title,
    };
  }
  if (media?.kind === "audio") return { kind: "audio", key: image.key, src: load.src, alt: image.alt, title: media.title };
  return {
    kind: "image",
    key: image.key,
    src: load.src,
    alt: image.alt,
    ...(image.width !== null ? { width: image.width } : {}),
    ...(image.height !== null ? { height: image.height } : {}),
  };
}

function sameSlide(a: ViewerSlide, b: ViewerSlide): boolean {
  if (a.kind === "pending") {
    return b.kind === "pending" && a.key === b.key && a.failed === b.failed && a.media === b.media;
  }
  if (a.kind === "video") {
    return (
      b.kind === "video" &&
      a.key === b.key &&
      a.src === b.src &&
      a.alt === b.alt &&
      a.poster === b.poster &&
      a.title === b.title
    );
  }
  if (a.kind === "audio") {
    return b.kind === "audio" && a.key === b.key && a.src === b.src && a.alt === b.alt && a.title === b.title;
  }
  return (
    b.kind === "image" &&
    a.key === b.key &&
    a.src === b.src &&
    a.alt === b.alt &&
    a.width === b.width &&
    a.height === b.height
  );
}

/**
 * Слайды по картинкам и состояниям. Сравнивается ГОТОВЫЙ вид слайда, а не
 * состояние загрузки: если получившийся слайд равен по полям слайду с тем же
 * ключом из `prev`, берётся объект из `prev`. Так `idle` в `loading` (оба
 * `pending`, `failed: false`) не плодит объекта.
 *
 * ⚠️ На этом держится увеличение текущей картинки: догрузка соседа меняет
 * массив, но не объект текущего слайда, и Zoom пакета, сбрасывающий увеличение
 * по смене `src` текущего, ничего не замечает (Р5 плана).
 */
export function slidesOf(
  images: readonly ViewerImage[],
  loads: ReadonlyMap<string, SlideLoad>,
  prev: readonly ViewerSlide[],
): ViewerSlide[] {
  const before = new Map(prev.map((slide) => [slide.key, slide]));
  return images.map((image) => {
    const made = slideOf(image, loads.get(image.key) ?? IDLE);
    const was = before.get(image.key);
    return was !== undefined && sameSlide(was, made) ? was : made;
  });
}

/**
 * Ключ, о котором пора сообщить «посмотрели»: текущий, загруженный и ещё не
 * сообщённый. Зовётся и на загрузку картинки, и на смену индекса: соседа пакет
 * грузит заранее, и когда он станет текущим, нового `load` уже не будет.
 *
 * Видео и аудио в `loaded` не попадают никогда: отметку ставит только `load`
 * у картинки в перехвате `ImageViewer.tsx`.
 */
export function viewedNow(
  current: string | null,
  loaded: ReadonlySet<string>,
  reported: ReadonlySet<string>,
): string | null {
  if (current === null || !loaded.has(current) || reported.has(current)) return null;
  return current;
}

/** Пропорции кадра, пока размер видео неизвестен: обычный 16:9. */
const FALLBACK_FRAME = { width: 16, height: 9 };

/**
 * Размер элемента видео по кадру (docs/homework-media-form-player-plan.md, Р2):
 * кадр вписан целиком в площадь слайда за вычетом подписи и зазора над ней и
 * полей `sideW` слева и справа: там лежат стрелки пакета (`--imgview-nav-w`).
 * Элемент ровно по кадру, а не во весь слайд: поля вокруг кадра остаются фоном
 * слайда, по которому листают, а полоса управления на Android лежит у кадра.
 * Неизвестный или нулевой размер кадра читается как 16:9; маленькое видео
 * растягивается до площади, крупное ужимается.
 */
export function fitBox(
  rect: { width: number; height: number },
  mediaW: number | null,
  mediaH: number | null,
  captionH: number,
  sideW: number,
): { width: number; height: number } {
  const known = mediaW !== null && mediaH !== null && mediaW > 0 && mediaH > 0;
  const w = known ? mediaW : FALLBACK_FRAME.width;
  const h = known ? mediaH : FALLBACK_FRAME.height;
  const availW = Math.max(0, rect.width - 2 * sideW);
  const availH = Math.max(0, rect.height - captionH);
  const scale = Math.min(availW / w, availH / h);
  return { width: Math.floor(w * scale), height: Math.floor(h * scale) };
}
